import { DateTime } from 'luxon';
import type { MouseEvent, ReactElement } from 'react';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { TabPill } from '../../components';
import { TabPills } from '../../components';
import type { BitrateHealth, BitratePoint, MuxRange } from './mux-health';
import { bitrateIncidents, bitrateStats } from './mux-health';

interface Props {
  /** Video-bitrate series for the selected range, oldest → newest. */
  series: BitratePoint[];
  /** Selected time range (controlled by the parent). */
  range: MuxRange;
  /** Called when the user picks a different range. */
  onRangeChange: (range: MuxRange) => void;
  /** Optional Mux dashboard URL for the "View Mux" link. */
  dashboardUrl?: string;
  /** Range pills; off for a finished event, whose window is fixed. Defaults to on. */
  showRanges?: boolean;
}

// Plot geometry. Height matches the SVG viewBox so overlay px == SVG units.
const CHART_H = 176;
const PAD_T = 16;
const PAD_B = 20;

const FILL_ID = 'bitrateFill';

/*
 * RGB triplets rather than full colours so the stroke, the hover dot and the
 * translucent area fill all come from one place per health level.
 */
const HEALTH_RGB: Record<BitrateHealth, string> = {
  good: '53, 211, 153',
  warn: '240, 180, 100',
  bad: '255, 138, 147'
};

const RANGES: { key: MuxRange; label: string }[] = [
  { key: 'live', label: 'Live' },
  { key: '15m', label: '15m' },
  { key: '1h', label: '1h' },
  { key: 'full', label: 'Full' }
];

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const fmtTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso);

  return dt.isValid ? dt.toFormat('h:mm a') : '';
};

const Card = styled.div`
  margin-top: 10px;
  padding: 14px 15px 11px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 13px;
  gap: 10px;
`;

const MuxLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  font-size: 14px;
  color: rgb(240, 242, 245);
  text-decoration: none;

  i {
    font-size: 13px;
  }

  &:hover,
  &:focus {
    outline: none;
    color: rgb(255, 255, 255);
    text-decoration: none;
  }
`;

const HeadRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const RangeText = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  font-size: 9px;
  letter-spacing: 0.02em;
  color: rgba(176, 186, 209, 0.62);
  white-space: nowrap;
`;

const Chart = styled.div`
  .inner-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .scale {
    color: rgba(176, 186, 209, 0.5);
    font-weight: 500;
    font-size: 11px;
  }

  .current {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 5px 11px;
    border-radius: 8px;
    background: rgba(8, 14, 30, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .current .num {
    font-weight: 800;
    font-size: 20px;
    line-height: 1;
    color: rgb(255, 255, 255);
  }

  .current .unit {
    color: rgba(176, 186, 209, 0.6);
    font-weight: 600;
    font-size: 10px;
  }
`;

const Plot = styled.div<{ $rgb: string }>`
  position: relative;
  height: ${CHART_H}px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 7px;
  background: rgba(8, 14, 30, 0.4);
  overflow: hidden;
  cursor: crosshair;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  .floor {
    position: absolute;
    bottom: 5px;
    left: 7px;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
    font-size: 10px;
    color: rgba(176, 186, 209, 0.45);
    z-index: 1;
  }

  .cursor {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.18);
    transform: translateX(-0.5px);
    pointer-events: none;
    z-index: 2;
  }

  .dot {
    position: absolute;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ $rgb }): string => `rgb(${$rgb})`};
    box-shadow: ${({ $rgb }): string => `0 0 0 3px rgba(${$rgb}, 0.2)`};
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 3;
  }

  .dot.now {
    width: 7px;
    height: 7px;
    transform: translateY(-50%);
    box-shadow: ${({ $rgb }): string => `0 0 0 3px rgba(${$rgb}, 0.15)`};
  }

  .tip {
    position: absolute;
    transform: translateX(-50%);
    pointer-events: none;
    z-index: 4;
    white-space: nowrap;
    padding: 3px 8px;
    border-radius: 6px;
    font-family: 'JetBrains Mono', monospace;
    background: rgba(8, 14, 30, 0.92);
  }

  .tip.top {
    top: 10px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: rgb(255, 255, 255);
    font-weight: 700;
    font-size: 11px;
  }

  .tip.top em {
    margin-left: 3px;
    font-style: normal;
    color: rgba(176, 186, 209, 0.7);
    font-weight: 600;
    font-size: 9px;
  }

  .tip.bottom {
    bottom: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(213, 221, 238, 0.9);
    font-weight: 600;
    font-size: 10px;
  }
`;

const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding: 0px 2px;

  span {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
    font-size: 9px;
    color: rgba(176, 186, 209, 0.4);
  }
`;

export const BitrateChart = ({
  series,
  range,
  onRangeChange,
  dashboardUrl,
  showRanges = true
}: Props): ReactElement => {
  const [hover, setHover] = useState<number | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);

  // Colour follows how much of the window was degraded, not the raw incident count.
  const rgb = useMemo(() => HEALTH_RGB[bitrateIncidents(series).health], [series]);

  const geometry = useMemo(() => {
    const n = series.length;
    const { peak } = bitrateStats(series);
    // 20% of headroom over the peak, rounded up to the next 500.
    const niceMax = Math.max(500, Math.ceil((peak * 1.2) / 500) * 500);
    const usableH = CHART_H - PAD_T - PAD_B;
    const yOf = (kbps: number): number => PAD_T + (1 - kbps / niceMax) * usableH;
    const xOf = (i: number): number => (n > 1 ? (i / (n - 1)) * 100 : 0);

    const points = series.map((point, i) => `${xOf(i).toFixed(2)} ${yOf(point.kbps).toFixed(2)}`);
    const line = points.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt}`).join(' ');
    const area = n > 1 ? `${line} L 100 ${CHART_H} L 0 ${CHART_H} Z` : '';

    return { niceMax, yOf, xOf, line, area };
  }, [series]);

  const onMove = (event: MouseEvent<HTMLDivElement>): void => {
    const el = plotRef.current;

    if (!el || series.length < 2) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const fraction = clamp((event.clientX - rect.left) / rect.width, 0, 1);

    setHover(Math.round(fraction * (series.length - 1)));
  };

  const pills: TabPill[] = RANGES.map((item) => ({
    key: item.key,
    label: item.label,
    dot: item.key === 'live',
    active: range === item.key,
    onClick: (): void => {
      onRangeChange(item.key);
      setHover(null);
    }
  }));

  const first = series[0]?.time ?? '';
  const last = series[series.length - 1]?.time ?? '';
  const rangeText = first && last ? `${fmtTime(first)} – ${fmtTime(last)}` : '';
  const current = series[series.length - 1]?.kbps ?? 0;

  const ticks = [0, 25, 50, 75, 100].map((pct) => {
    const i = Math.round((pct / 100) * Math.max(0, series.length - 1));

    return { key: pct, label: fmtTime(series[i]?.time ?? '') };
  });

  const hoverPoint = hover !== null ? series[hover] : undefined;
  const hoverX = hoverPoint ? geometry.xOf(hover ?? 0) : 0;
  const tagX = clamp(hoverX, 12, 88);

  return (
    <Card>
      <Head>
        <MuxLink href={dashboardUrl ?? 'https://dashboard.mux.com'} target='_blank' rel='noreferrer'>
          View Mux
          <i className="fal fa-external-link"/>
        </MuxLink>

        <HeadRight>
          <RangeText>{rangeText}</RangeText>
          {showRanges && <TabPills pills={pills} small darker />}
        </HeadRight>
      </Head>

      <Chart>
        <div className='inner-head'>
          <span className='scale'>{geometry.niceMax.toLocaleString()} Kbps</span>
          <span className='current'>
            <span className='num'>{current.toLocaleString()}</span>
            <span className='unit'>Kbps</span>
          </span>
        </div>

        <Plot
          $rgb={rgb}
          ref={plotRef}
          onMouseMove={onMove}
          onMouseLeave={(): void => {
            setHover(null);
          }}
        >
          <svg viewBox={`0 0 100 ${CHART_H}`} preserveAspectRatio='none'>
            <defs>
              <linearGradient id={FILL_ID} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={`rgba(${rgb}, 0.22)`} />
                <stop offset='100%' stopColor={`rgba(${rgb}, 0)`} />
              </linearGradient>
            </defs>
            {geometry.area && <path d={geometry.area} fill={`url(#${FILL_ID})`} />}
            {geometry.line && (
              <path
                d={geometry.line}
                fill='none'
                stroke={`rgb(${rgb})`}
                strokeWidth={2}
                strokeLinejoin='round'
                strokeLinecap='round'
                vectorEffect='non-scaling-stroke'
              />
            )}
          </svg>

          <span className='floor'>0 Kbps</span>

          {hoverPoint ? (
            <>
              <span className='cursor' style={{ left: `${hoverX}%` }} />
              <span
                className='dot'
                style={{ left: `${hoverX}%`, top: `${geometry.yOf(hoverPoint.kbps)}px` }}
              />
              <span className='tip top' style={{ left: `${tagX}%` }}>
                {hoverPoint.kbps.toLocaleString()}
                <em>Kbps</em>
              </span>
              <span className='tip bottom' style={{ left: `${tagX}%` }}>
                {fmtTime(hoverPoint.time)}
              </span>
            </>
          ) : (
            <span className='dot now' style={{ right: '2px', top: `${geometry.yOf(current)}px` }} />
          )}
        </Plot>

        <Axis>
          {ticks.map((tick) => (
            <span key={tick.key}>{tick.label}</span>
          ))}
        </Axis>
      </Chart>
    </Card>
  );
};
